import {
  CtaBuilderFn,
  InstantAnswerBuilderFn,
  InstantAnswerContext,
  InstantAnswerScenario,
} from "@faire/help-center-self-serve/types";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { intlDate } from "@faire/web--source/common/localization/date-fns/intlDate";
import isEmpty from "lodash/isEmpty";

import {
  MessageRetailerOrBrandButton,
  additionalDataRequiredForContactingRetailerOrBrandCTA,
} from "../../shared/instantAnswerUtils";
import { isBrandOrderBackordered } from "../../utils/isBrandOrderBackordered";

import { CheckShipmentStatusCTA, GoToOrderDetailsCTA } from "./ctas";
import { NEED_MORE_INFO_ANSWER } from "./shared";

export const WhereIsMyOrderIA: InstantAnswerBuilderFn = (strictLocalize) => {
  return {
    titleBuilder: (context) => {
      return getAnswer(context, strictLocalize).getTitle();
    },

    descriptionBuilder: (context) => {
      return getAnswer(context, strictLocalize).getDescription();
    },

    primaryCtaBuilder: {
      textBuilder: (context) => {
        return getPrimaryCTA(context, strictLocalize)?.label;
      },
      linkBuilder: (context) => {
        return getPrimaryCTA(context, strictLocalize)?.getLink?.(
          context.brandOrder
        );
      },
    },
    secondaryCtaBuilder: {
      textBuilder: (context) => {
        return MessageRetailerOrBrandButton(context, strictLocalize)?.label;
      },
      onClickFunctionBuilder: (context) => {
        return MessageRetailerOrBrandButton(context, strictLocalize)?.onClick;
      },
    },

    additionalRequiredData:
      additionalDataRequiredForContactingRetailerOrBrandCTA(),
  };
};

const getAnswer = (
  context: InstantAnswerContext,
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => {
  const selectedBrandOrder = context.brandOrder?.brand_order;
  const retailerShippingContext = context.shippingContext;
  if (!selectedBrandOrder || !retailerShippingContext) {
    return NEED_MORE_INFO_ANSWER(strictLocalize);
  }

  if (isBrandOrderBackordered(selectedBrandOrder)) {
    return BACKORDERED_ANSWER(strictLocalize);
  } else if (!isEmpty(retailerShippingContext.shipments)) {
    return TRACKING_AVAILABLE_ANSWER(strictLocalize);
  } else {
    const expectedShipDate =
      retailerShippingContext.expected_ship_window?.start_date ??
      retailerShippingContext.estimated_ship_date_upper_bound;

    return expectedShipDate && expectedShipDate > Date.now()
      ? SHIPMENT_ESTIMATE_ANSWER(strictLocalize, expectedShipDate)
      : NO_TRACKING_INFO_ANSWER(strictLocalize);
  }
};

const getPrimaryCTA: CtaBuilderFn = (context, strictLocalize) => {
  const brandOrder = context.brandOrder;
  const selectedBrandOrder = brandOrder?.brand_order;
  const retailerShippingContext = context.shippingContext;
  if (!brandOrder || !selectedBrandOrder || !retailerShippingContext) {
    return undefined;
  }

  if (isBrandOrderBackordered(selectedBrandOrder)) {
    return GoToOrderDetailsCTA(strictLocalize);
  } else if (!isEmpty(retailerShippingContext.shipments)) {
    return CheckShipmentStatusCTA(strictLocalize);
  } else {
    return undefined;
  }
};

const TRACKING_AVAILABLE_ANSWER = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => ORDER_SHIPPED(strictLocalize),
  getDescription: () => [CHECK_SHIPMENT_STATUS(strictLocalize)],
});

const BACKORDERED_ANSWER = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => ITEMS_BACKORDERED(strictLocalize),
  getDescription: () => [VISIT_ORDER_DETAILS(strictLocalize)],
});

const SHIPMENT_ESTIMATE_ANSWER = (
  strictLocalize: StrictLocalizeFunction,
  expectedShipDate: number
): InstantAnswerScenario => ({
  getTitle: () => SHIPMENT_ESTIMATE(strictLocalize, expectedShipDate),
  getDescription: () => [MESSAGE_BRAND_FOR_ORDER_QUESTIONS(strictLocalize)],
});

const NO_TRACKING_INFO_ANSWER = (
  strictLocalize: StrictLocalizeFunction
): InstantAnswerScenario => ({
  getTitle: () => TRACKING_INFO_UNAVAILABLE(strictLocalize),
  getDescription: () => [NO_SHIPMENT_TRACKING_NUMBER(strictLocalize)],
});

const ORDER_SHIPPED = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "Your order has been shipped!",
    description: {
      text: "Title for the answer of where is my order issue if the selected order has been shipped",
    },
  });

const CHECK_SHIPMENT_STATUS = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage:
      "You can check the shipment status of your order using the button below.",
    description: {
      text: "Description for the answer of issues in the track shipment sub issue tree if the selected order has been shipped",
    },
  });

const ITEMS_BACKORDERED = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage:
      "The brand set some or all of the items in your order as backordered",
    description: {
      text: "Title for the answer of issues in the track shipment sub issue tree if the selected order has been backordered",
    },
  });

const VISIT_ORDER_DETAILS = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "Visit the Order Details page for more details anytime.",
    description: {
      text: "Description for the answer of issues in the track shipment sub issue tree if the selected order has been backordered",
    },
  });

const SHIPMENT_ESTIMATE = (
  strictLocalize: StrictLocalizeFunction,
  expectedShipDate: number
) =>
  strictLocalize(
    {
      defaultMessage:
        "Your order is expected to be shipped on {expectedShipDate}",
      description: {
        text: "Title for the answer of estimated ship date issue if the selected order has not been shipped and the estimated ship date is in the future",
      },
    },
    {
      expectedShipDate: expectedShipDate
        ? intlDate(expectedShipDate, "PP")
        : "(unknown)",
    }
  );

const MESSAGE_BRAND_FOR_ORDER_QUESTIONS = (
  strictLocalize: StrictLocalizeFunction
) =>
  strictLocalize({
    defaultMessage:
      "If you have any questions about your order in the meantime, message the brand by clicking the button below or contact us if you need more help.",
  });

const TRACKING_INFO_UNAVAILABLE = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage: "Tracking information is unavailable",
    description: {
      text: "Title for the answer of issues in the track shipment sub issue tree if the selected order has not been shipped and the estimated ship date is in the past",
    },
  });

const NO_SHIPMENT_TRACKING_NUMBER = (strictLocalize: StrictLocalizeFunction) =>
  strictLocalize({
    defaultMessage:
      "The brand hasn’t provided a shipment tracking number. This might be because the item hasn’t been shipped yet. Message the brand by clicking the button below or contact us if you need more help.",
  });
